import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  Box,
  Menu,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { DocumentTemplate } from "../../../../stores/flowsTemplatesStore";
import { TemplateFileUpload } from "./TemplateFileUpload";
import { PDFEditor } from "./PDFEditor/PDFEditor";
import { EditTemplateModal } from "./EditTemplateModal/EditTemplateModal";
import { useTemplatesTable } from "./useTemplatesTable";
import ConfirmationModal from "../../../../components/Modal/ConfirmationModal";

interface TemplatesTableProps {
  templates: DocumentTemplate[];
}

const formatTemplateType = (type: string) => {
  return type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

export const TemplatesTable: React.FC<TemplatesTableProps> = ({ templates }) => {
  const {
    anchorEl,
    selectedTemplateId,
    selectedTemplate,
    uploadDialogOpen,
    pdfEditorOpen,
    pdfEditorTemplateId,
    confirmDeleteOpen,
    editModalOpen,
    editingTemplate,
    deleteLoading,
    isPdfFile,
    handleMenuOpen,
    handleMenuClose,
    handleUpload,
    handleEditPlaceholders,
    handleClosePdfEditor,
    handleOpenEditModal,
    handleCloseEditModal,
    handleOpenDeleteConfirm,
    handleCloseDeleteConfirm,
    handleConfirmDelete,
    handleCloseUploadDialog,
  } = useTemplatesTable(templates);

  if (templates.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <InsertDriveFileIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
        <Typography color="text.secondary">
          No templates created yet. Click "Create Template" to get started.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>File</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 100 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((template) => (
              <TableRow key={template.identifier} hover data-testid={`template-row-${template.identifier}`}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 120 }}>
                    {template.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={formatTemplateType(template.template_type)}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                </TableCell>
                <TableCell>
                  {template.file_name ? (
                    <Tooltip title={template.file_name}>
                      <Chip
                        icon={<InsertDriveFileIcon />}
                        label={template.file_name.length > 15
                          ? `${template.file_name.substring(0, 12)}...`
                          : template.file_name}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </Tooltip>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      No file
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                    <Tooltip title="Edit / Upload / Placeholders">
                      <IconButton
                        size="small"
                        data-testid={`edit-template-btn-${template.identifier}`}
                        onClick={(e) => handleMenuOpen(e, template.identifier)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        data-testid={`delete-template-btn-${template.identifier}`}
                        onClick={() => handleOpenDeleteConfirm(template.identifier)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedTemplate) {
            handleOpenEditModal(selectedTemplate);
          }
          handleMenuClose();
        }} data-testid="menu-edit-name">
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit Name/Type
        </MenuItem>
        <MenuItem onClick={handleUpload}>
          <UploadFileIcon sx={{ mr: 1 }} fontSize="small" />
          Upload File
        </MenuItem>
        {isPdfFile && (
          <MenuItem onClick={handleEditPlaceholders} data-testid="menu-edit-placeholders">
            <InsertDriveFileIcon sx={{ mr: 1 }} fontSize="small" />
            Edit Placeholders
          </MenuItem>
        )}
      </Menu>

      {/* Upload Dialog */}
      {selectedTemplateId && (
        <TemplateFileUpload
          open={uploadDialogOpen}
          templateId={selectedTemplateId}
          onClose={handleCloseUploadDialog}
        />
      )}

      {/* Edit Template Modal */}
      <EditTemplateModal
        open={editModalOpen}
        template={editingTemplate}
        onClose={handleCloseEditModal}
      />

      {/* PDF Editor Dialog */}
      {pdfEditorTemplateId && (
        <Dialog
          open={pdfEditorOpen}
          onClose={handleClosePdfEditor}
          maxWidth="xl"
          fullWidth
          PaperProps={{ sx: { height: '90vh' } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            Edit PDF Placeholders
            <IconButton onClick={handleClosePdfEditor} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 2, height: 'calc(100% - 64px)' }}>
            <PDFEditor templateId={pdfEditorTemplateId} onClose={handleClosePdfEditor} />
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={confirmDeleteOpen}
        title="Delete Template"
        message="Are you sure you want to delete this template? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseDeleteConfirm}
        severity="error"
        submitLoading={deleteLoading}
      />
    </>
  );
};
